import { Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { SubSink } from 'subsink';
import { ProfileApiService } from '../../apiCalls/profile-api.service';
import { CustomSnackbarService } from '../../shared/custom-snackbar.service';
import { RoutesConst } from '../../shared/route-const';

interface EditCompanyForm {
  companyName: FormControl<string>;
  address: FormControl<string>;
  zipCity: FormControl<string>;
  country: FormControl<string>;
  phoneNumber: FormControl<string>;
}

export interface EditCompanyFormValue {
  companyName: string;
  address: string;
  zipCity: string;
  country: string;
  phoneNumber: string;
}

@Component({
  selector: 'app-edit-company',
  templateUrl: './edit-company.component.html',
  styleUrl: './edit-company.component.scss',
  imports: [MatButtonModule, MatDialogModule, MatFormField, MatInputModule, ReactiveFormsModule],
})
export class EditCompanyComponent implements OnDestroy {
  private readonly subs = new SubSink();

  // Injected dependencies
  private readonly profileApiSvc = inject(ProfileApiService);
  private readonly companyInfo = this.profileApiSvc.getCompanyProfile();

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly customSnackbarSvc = inject(CustomSnackbarService);

  /** Reactive signals for state management */
  apiErrorMessage = signal<string>('');
  saveState = signal<boolean>(false);

  /** Form definition */
  form: FormGroup<EditCompanyForm> = this.fb.nonNullable.group({
    companyName: ['', Validators.required],
    address: ['', Validators.required],
    zipCity: ['', Validators.required],
    country: ['', Validators.required],
    phoneNumber: [''],
  });

  constructor() {
    effect(() => {
      const data = this.companyInfo();
      if (data) {
        this.form.patchValue({
          companyName: data.CompanyName,
          address: data.Address,
          zipCity: data.ZipCity,
          country: data.Country,
          phoneNumber: data.PhoneNumber,
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue: EditCompanyFormValue = this.form.getRawValue();

    this.saveState.set(true);
    this.clearErrorMessage();

    this.customSnackbarSvc.requestSnackbar();

    this.subs.sink = this.profileApiSvc.updateCompanyProfile(formValue).subscribe({
      next: () => this.handleSuccess(),
      error: (error) => this.handleError(error),
    });
  }

  /**
   * Handles successful
   */
  private handleSuccess(): void {
    this.clearErrorMessage();
    this.saveState.set(false);
    this.routeBack();
    this.customSnackbarSvc.successSnackbar('Thank you for updating the company profile. It may take up to one business day to process the request.');
  }

  /**
   * Handles errors
   */
  private handleError(error: any): void {
    const errorMessage = error?.message || 'Unknown error occurred';
    this.apiErrorMessage.set(`: ${errorMessage}`);
    console.error('Error:', errorMessage);
    this.customSnackbarSvc.errorSnackbar();
    this.saveState.set(false);
  }

  /**
   * Clears the API error message
   */
  private clearErrorMessage(): void {
    this.apiErrorMessage.set('');
  }

  routeBack() {
    this.router.navigate([RoutesConst.companyProfile]);
  }

  // Getters for easier access in the template
  get companyName() {
    return this.form.controls.companyName;
  }
  get phoneNumber() {
    return this.form.controls.phoneNumber;
  }
}
